import { CanvasData } from "@/components/canvas/types/canvasTypes";

export type MyFirmListItem = { firmGuid: string; firmName: string };

export type Menu = {
  id: number;
  menuGroup_Id?: number;
  menuGroupName: string;
  menuId: number;
  menuName: string;
  order: number;
  menuIcon: string;
  path: string;
  adminOnly: boolean;
  description?: string;
};

export type MenuDetails = Menu;

export type MenuRight = Menu & {
  menuGroupId?: number;
};

export type MenuHeader = {
  id: number;
  name: string;
  order: number;
};

export type MenuHeaderDetails = MenuHeader;

export type MenuHeaderMember = {
  id: number;
  menuHeader_Id: number;
  menu_Id: number;
};

export type SpecialEntity =
  | "DsTemplate"
  | "DsStockTemplate"
  | "Content"
  | "ContentGroup"
  | "ContentGroupMemberCategory"
  | "Channel"
  | "Campaign"
  | "Player"
  | "Area"
  | "AreaType"
  | "ScreenSize"
  | "Tag"
  | "Store"
  | "HMenu"
  | "Log/AuditLog"
  | "Log/LoginLog"
  | "Log/TraceLog"
  | "Currency"
  | "Role"
  | "User"
  | "MenuHeader"
  | "Menu"
  | "Firm"
  | "FirmLicence"
  | "UserSystemDynamicSetting"
  | "UserSubAdmin"
  | "BlogPost";

export type SpecialRight = "List" | "Add" | "Delete" | "GetDetails" | "Update";

export type ApiRight = {
  menuId: number;
  apiEndPoint: `v1/${string}`;
};

export type MenuArchitectureItem = {
  menuHeaderId: number;
  menuHeaderName: string;
  order: number;
  menuHeaderMenuList: {
    menuId: number;
    menuName: string;
  }[];
};

export type UserRight = {
  userDetails: {
    userId: string;
    name: string;
    email: string;
    isSuperAdmin: boolean;
  };
  menuRights: MenuRight[];
  menuArchitecture?: MenuArchitectureItem[];
  apiRights: ApiRight[];
  isAdmin: boolean;
};

export type Breadcrumb = {
  name: string;
  path: string;
  component?: React.ReactNode;
};

export type AuditLog = {
  id: number;
  columnName: string;
  newValue: string;
  originalValue: string;
  recordID: number;
  tableName: string;
  time: string;
  type: string;
  userEmail: string;
};

export type LoginLog = {
  id: number;
  time: string;
  userEmail: string;
};

export type TraceLog = {
  id: number;
  path: string;
  body: string;
  traceIdentifier: string;
  scheme: number;
  remoteIPAddress: string;
  remotePort: string;
  localIPAddress: string;
  localPort: string;
  method: string;
  userEmail: string;
  time: string;
};

export type Currency = {
  id: number;
  currencyCode: string;
  currencyName: string;
  name: string;
  symbol: number;
};

export type ContentType =
  | "image/*"
  | "video/*"
  | "audio/*"
  | "link"
  | "split"
  | "application/pdf"
  | "dynamic/fromTemplate";

export type Content = {
  contentGuid: string;
  contentStorageName: string;
  contentType: ContentType;
  name: string;
  folderGuid?: string;
  intId: number;
  contentLink: string;
  startDate?: string;
  endDate?: string;
  // Split content layout config or dynamic from template layout config
  layout_config?: string;
  screenSizeId: string;
  screenSizeintId: number;
  screenSizeName?: string;
  duration?: number;
  // Template layout config
  templateLayoutConfig?: string;
  contentSize: number;
};

export type ContentFolder = {
  id: string;
  name: string;
  color: string;
  mainFolderId?: string;
  order?: number;
};

export type ContentDownload = {
  fileContents: string;
  contentType: string;
  fileDownloadName: string;
  lastModified: number;
  entityTag: string;
  enableRangeProcessing: boolean;
};

export type ContentTag = {
  contentId: string;
  contentintId: number;
  id: string;
  intId: number;
  tagId: string;
  tagName: string;
  tagintId: number;
};

export type ContentGroupMemberCategory = {
  id: string;
  intId: number;
  name: string;
};

export type ContentGroupTag = {
  contentGroupId: string;
  contentGroupintId: number;
  id: string;
  intId: number;
  tagId: string;
  tagName: string;
  tagintId: number;
};

export enum ContentGroupType {
  Linear = 0,
  Robot = 3,
}

export type ContentGroup = {
  contentGroupGuid: string;
  lastPublish: string;
  licences: string;
  name: string;
  totalActiveContent: number;
  totalInactiveContent: number;
  isShuffle: boolean;
  intId: number;
  screenSizeId: string;
  screenSizeName: string;
  screenSizeintId: number;
  type: ContentGroupType;
  // TODO remove
  typeString: "Linear" | "Robot";
};

export enum TargetAge {
  A0 = 0,
  A018 = 18,
  A1929 = 1929,
  A3039 = 3039,
  A4049 = 4049,
  A5059 = 5059,
  A60100 = 60100,
}

export enum TargetEmotion {
  E0 = 0,
  happy = 10,
  neutral = 20,
  sad = 30,
  surprize = 40,
  angry = 50,
}

export enum TargetGender {
  Male = 0,
  Female = 1,
}

export type ContentGroupMember = {
  contentGroupGuid: string;
  contentGroupMemberGuid: string;
  contentGuid: string;
  contentType: string;
  contentStorageName: string;
  duration: number;
  isActive: boolean;
  name: string;
  order: number;
  startDate: string;
  endDate: string;
  layoutConfig: string;
  contentLink: string;
  viewportSize: string;
  cameraAge: TargetAge;
  cameraAgeString: string;
  cameraEmotion: TargetEmotion;
  cameraEmotionString: string;
  gender: TargetGender;
  genderString: string;
  contentGroupMemberCategoryGuid?: string;
  contentGroupMemberCategoryName?: string;
};

export type LinkedLicence = {
  firmGuid: string;
  intId: number;
  firmLicenceGuid: string;
  playerName: string | null;
  licenceType: string;
  licenceExpiry: string;
};

export type FirmApiToken = {
  expiry: string;
  status: string;
  token: string;
};

export type AxDataTableRequestInput = {
  numberOfObjectsPerPage: number;
  pageNumber: number;
  search?: string;
  references?: Record<string, string>;
};

export type AxDataTableDynamicRequestParams = Record<string, string | number | boolean | undefined>;

export type AxDataTableResponse<T> = {
  dropdownList: number[];
  listOfColumns: string[];
  numberOfObjectsPerPage: number;
  pageList: unknown;
  pageNumber: number;
  pageTotal: number;
  totalRecord: number;
  result: T[];
};

export type Role = {
  id: number;
  roleName: string;
};

export type RoleMenu = {
  id: number;
  menuName: string;
  menu_Id: number;
  roleName: string;
  role_Id: number;
};

export type Entity = {
  id: number;
  name: string;
};

export type MenuEntity = {
  entityName: string;
  entity_Id: number;
  id: number;
  menuName: string;
  menu_Id: number;
  right: number;
  rightName: string;
};

export type RightEnum = {
  value: number;
  name: string;
};

export type Allergy = {
  id: number;
  name: string;
};

export type Store = {
  id: string;
  name: string;
  currency: string;
};

export type StoreDetails = Store & {
  firmGuid: string;
  currency_Id: number;
  customerKey: string;
};

export type StoreSalePack = {
  id: string;
  customerKey: string;
  name: string;
};

export type HospitalityMenu = {
  currencyName: string;
  firmName: string;
  id: number;
  lastSync: string;
  name: string;
  pushId: number;
  store: string;
};

export type HospitalityMenuDetails = HospitalityMenu & {
  currency_Id: number;
  autoSyncOn: boolean;
  autoSyncPeriodMinute: string;
};

export type HospitalityMenuFile = {
  id: number;
  contentName: string;
  contentStorageName: string;
  contentType: string;
  created: string;
  time: string;
};

export type HospitalityMenuCategory = {
  id: number;
  name: string;
  hMenu: null;
  hMenu_Id: number;
  order: number;
  externalId: null;
  hMenuItem: unknown[];
  createdDateTime_UTC: string;
  lastModifiedDate_UTC: string;
  createdBy: unknown;
  createdBy_Id: number;
  lastUpdatedBy: unknown;
  lastUpdatedBy_Id: number;
  dataStatus: number;
  rowVersion: string;
  guid: string;
};

export type HospitalityMenuCategoryItem = {
  id: number;
  description: string;
  hMenuCategoryName: string;
  hMenuCategory_Id: number;
  name: string;
  price: number;
};

export type HospitalityMenuCategoryItemAllergy = {
  id: number;
  allergyId: number;
  itemId: number;
};

export type User = {
  id: number;
  guid?: string;
  email: string;
  firms: string;
  name: string;
  rights: string;
  surname: string;
};

export type UserDetails = User & {
  mobile: string;
  gender: string;
  guid: string;
};

export type UserFirm = {
  id: number;
  user_Id: number;
  firm_Id: number;
  guid: string;
};

export type AppUserRole = {
  id: number;
  appUser_Id: number;
  role_Id: number;
};

export type Firm = {
  id: number;
  firmGuid: string;
  name: string;
  totalUsers: number;
  totalContent: number;
  totalContentGroup: number;
};

export type FirmDetails = Firm & {
  apiMenu: false;
  customerKey?: string;
  firmGuid: string;
  systemEndPoint?: string;
  systemPassword?: string;
  systemUsername?: string;
  totalLicence: number;
  totalWebLinkLicence: number;
};

export type FirmDashboard = {
  name: string;
  totalActivePlayers: number;
  totalAppLicence: number;
  totalAssignedLicence: number;
  totalChannel: number;
  totalContent: number;
  totalContentAudio: number;
  totalContentGroup: number;
  totalContentImg: number;
  totalContentLink: number;
  totalContentSplit: number;
  totalContentVideo: number;
  totalExpiredLicence: number;
  totalNotAssignedLicence: number;
  totalPassivePlayers: number;
  totalPlayer: number;
  totalPlayers: number;
  totalUsers: number;
  totalWebLinkLicence: number;
  totalHealthyDevice: number;
  totalUnhealthyDevice: number;
  totalContentSize: number;
  totalViewLastDay: number;
  totalViewLastHour: number;
};

export type TemplateResponse = {
  id: string;
  name: string;
  viewport_size: string;
  canvas_data: string;
  created_at: string;
  updated_at: string;
  firmGuid: string;
};

export type StockTemplateResponse = {
  id: string;
  name: string;
  viewport_size: string;
  canvas_data: string;
  created_at: string;
  updated_at: string;
  category_name: string;
};

export type Template = {
  id: string;
  name: string;
  viewport_size: string;
  canvas_data: CanvasData;
  created_at: string;
  updated_at: string;
  firmGuid?: string;
  categoryName?: string;
};

export type Tag = {
  id: string;
  intId: number;
  name: string;
};

export type ScreenSize = {
  id: string;
  intId: number;
  name: string;
  width: number;
  height: number;
};

export type ScreenSizeDetails = ScreenSize & {};

export type Player = {
  areaId: string;
  areaintId: number;
  channelId: string;
  channelintId: number;
  code: string;
  contentGroupId: string;
  contentGroupintId: number;
  contentId: string;
  contentintId: number;
  deviceId: string;
  deviceType: string;
  firmLicenceId: string;
  firmLicenceintId: number;
  id: string;
  idiom: string;
  intId: number;
  manufacturer: string;
  name: string;
  platform: string;
  screenSizeId: string;
  screenSizeintId: number;
  screenSizeName: string;
  registerDate: string;
  registerStatus: number;
  registerStatusString: string;
  timezone: string;
  uniqueId: string;
  versionString: string;
  defaultContentId: string;
  defaultContentintId: number;
  lastPublish: string;
  ipAddress?: string;
  latitude?: number;
  longitude?: number;
};

export type PlayerTag = {
  playerId: string;
  playerintId: number;
  id: string;
  intId: number;
  tagId: string;
  tagName: string;
  tagintId: number;
};

export type PlayerDetails = {
  id: string;
  channelId: string;
  intId: number;
  name: string;
  channelintId: number;
  contentGroupintId: number;
  contentintId: number;
  defaultContentintId: number;
  firmLicenceintId: number;
  screenSizeintId: number;
  timezone: string;
  areaId: string;
  areaintId: number;
  lastPublish: string;
  code: string;
  deviceType?: string;
  ipAddress?: string;
  latitude?: number;
  longitude?: number;
  versionString?: string;
  healthSequence?: number;
};

export type Channel = {
  id: string;
  intId: number;
  name: string;
  isCombineAndShuffle: boolean;
  defaultContentId: string;
  defaultContentintId: number;
  screenSizeintId: number;
  screenSizeId: string;
  screenSizeName: string;
  lastPublish: string;
};

export type ChannelTag = {
  channelId: string;
  channelintId: number;
  id: string;
  intId: number;
  tagId: string;
  tagName: string;
  tagintId: number;
};

export type ChannelDetails = Channel;

export type ChannelDetailItem = {
  channelId?: string;
  channelintId?: number;
  dayOfWeek?: DayOfWeek;
  id: string;
  intId: number;
  isActive: boolean;
  order: number;
  periodEnd: string;
  periodStart: string;
};

export type ChannelDetailItemContent = {
  id: string;
  intId: number;
  channelDetailId: string;
  channelDetailintId: number;
  contentId: string;
  contentintId: number;
  contentGroupId: string;
  contentGroupintId: number;
};

export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export type ChannelDetailDate = {
  id: string;
  intId: number;
  channelDetailId: string;
  channelDetailintId: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
};

export type Area = {
  id: string;
  intId: number;
  motherAreaintId: number; // mother area id is the parent of this area
  name: string;
  timezone: string;
  contentGroupId?: string;
  contentGroupintId?: number;
  contentId?: string;
  contentintId?: number;
  areaTypeintId: number;
  overwriteSub: boolean;
  defaultContentId?: string;
  defaultContentintId?: number;
};

export type AreaDetails = Area;

export type AreaContent = {
  id: string;
  intId: number;
  areaId: string;
  areaintId: number;
  contentId: string;
  contentintId: number;
  contentGroupId: string;
  contentGroupintId: number;
};

export enum CampaignType {
  Standard = 0,
  Conditional = 10,
}

export type Campaign = {
  id: string;
  intId: number;
  name: string;
  isCombineAndShuffle: boolean;
  defaultContentId: string;
  defaultContentintId: number;
  lastPublish: string;
  campaignType: CampaignType;
  campaignTypeString: "Standard" | "Conditional";
};

export type CampaignCondition = {
  id: string;
  Campaign_Id: number;
  cameraAges: [{ value: TargetAge }];
  genders: [{ value: TargetGender }];
  cameraEmotions: [{ value: TargetEmotion }];
  areas: [{ intId: number }];
};

export type CampaignTag = {
  campaignId: string;
  campaignintId: number;
  id: string;
  intId: number;
  tagId: string;
  tagName: string;
  tagintId: number;
};

export type CampaignDetails = Campaign;

export type CampaignDetailItem = {
  campaignId: string;
  campaignintId: number;
  dayOfWeek?: DayOfWeek;
  id: string;
  intId: number;
  periodEnd: string;
  periodStart: string;
  isActive: boolean;
  contentGroupintId?: number;
  withCustomWeekDays: boolean;
};

export type CampaignDetailItemContent = {
  id: string;
  intId: number;
  campaignDetailId: string;
  campaignDetailintId: number;
  contentId: string;
  contentintId: number;
};

export type CampaignDetailDate = {
  id: string;
  intId: number;
  campaignDetailId: string;
  campaignDetailintId: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
};

export type PlayerCampaign = {
  id: string;
  intId: number;
  playerId: string;
  playerintId: number;
  campaignId: string;
  campaignintId: number;
};

export type AreaCampaign = {
  id: string;
  intId: number;
  areaId: string;
  areaintId: number;
  campaignId: string;
  campaignintId: number;
};

export type AreaType = {
  id: string;
  intId: number;
  name: string;
  color: string;
  extra: string;
};

export type AreaTypeDetails = AreaType;

export type Api = {
  id: number;
  endPoint: string;
};

export type MenuApi = {
  id: number;
  menu_Id: number;
  api_Id: number;
};

export type BlogPost = {
  id: string;
  title: string;
  content: string;
  created_at: string;
};

export type BlogPostDetails = BlogPost;

export type PlayerSyncStatus = {
  status: "synced" | "not-synced";
  code?: string;
  tempSessionCode: string;
};

export enum PlayerHealth {
  uninitialized = 0,
  ok = 1,
  unhealthy = 2,
}

export type PlayerStats = {
  contentId: number;
  createdAt: string;
  id: string;
  playerId: number;
};

export type Notification = {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  createdAt: string;
};

export type SearchResponseItem = {
  id: string;
  name: string;
  type: "content" | "contentGroup" | "channel" | "campaign" | "player";
};

export type SystemDynamicSetting = {
  id: string;
  name: string;
};
